import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Check, Lock, User, Eye, EyeOff } from 'lucide-react';
import Navbar from './Navbar';

const API_BASE_URL = 'https://api.4fingerstudios.com';

const CyanText = ({ text, className }) => (
  <span className={`relative inline-block ${className} text-cyan-400 ${glowing ? 'animate-pulse' : ''}`}>
    {text}
  </span>
);

const Input = ({ icon: Icon, className = '', ...props }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-400/50" size={20} />
    <input
      {...props}
      className={`w-full bg-black/60 border border-cyan-400/20 pl-10 p-3 text-slate-300 focus:border-cyan-400 transition-colors duration-300 rounded ${className}`}
    />
  </div>
);

const PasswordInput = ({ icon = Lock, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input {...props} icon={icon} type={show ? 'text' : 'password'} />
      <button
        type="button"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400"
        onClick={() => setShow(prev => !prev)}
      >
        {show ? <EyeOff size={20} /> : <Eye size={20} />}
      </button>
    </div>
  );
};

const LoginPage = () => {
  const [formState, setFormState] = useState('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState({ text: '', type: '' });
  const [glowing, setGlowing] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            navigate('/user-profile');
          }
        } catch (error) {
          console.error('Auth check failed:', error);
        }
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlowing(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const checkPasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (/[A-Z]/.test(pass)) strength++;
    if (/[0-9]/.test(pass)) strength++;
    if (/[^A-Za-z0-9]/.test(pass)) strength++;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const pass = e.target.value;
    setPassword(pass);
    checkPasswordStrength(pass);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFeedbackMessage({ text: '', type: '' });

    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      const clientIp = ipData.ip;

      let response;
      let responseText;

      if (formState === 'signup') {
        if (!username || !email || !password) {
          throw new Error('Please fill in all required fields.');
        }

        response = await fetch(`${API_BASE_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Client-IP': clientIp
          },
          body: JSON.stringify({ username, password, email })
        });

        responseText = await response.text();

        if (!response.ok) {
          throw new Error(responseText || 'Signup failed');
        }

        setFeedbackMessage({ text: responseText || 'Account created! Please verify your email.', type: 'success' });
        setTimeout(() => navigate('/verify-email', { state: { username } }), 2000);
        setUsername('');
        setEmail('');
        setPassword('');
      } else if (formState === 'login') {
        if (!username || !password) {
          throw new Error('Please enter both username and password.');
        }

        response = await fetch(`${API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Client-IP': clientIp
          },
          body: JSON.stringify({ username, password })
        });

        responseText = await response.text();

        if (!response.ok) {
          if (response.status === 403 && responseText.includes('Email not verified')) {
            setFeedbackMessage({ text: 'Email not verified. Redirecting to verification...', type: 'error' });
            setTimeout(() => navigate('/verify-email', { state: { username } }), 2000);
          } else {
            throw new Error(responseText || 'Login failed');
          }
          return;
        }

        const contentType = response.headers.get('Content-Type');
        let responseData = contentType?.includes('application/json') ? JSON.parse(responseText) : null;

        if (!responseData?.token) {
          throw new Error('No token received in response');
        }

        localStorage.setItem('token', responseData.token);

        const validateResponse = await fetch(`${API_BASE_URL}/api/auth/validate`, {
          headers: { 
            'Authorization': `Bearer ${responseData.token}`,
            'X-Client-IP': clientIp
          },
        });

        const validateText = await validateResponse.text();
        const validateData = validateResponse.headers.get('Content-Type')?.includes('application/json') ? JSON.parse(validateText) : null;

        if (!validateResponse.ok || !validateData) {
          throw new Error(validateText || 'Token validation failed');
        }

        navigate('/user-profile');
      }
    } catch (error) {
      setFeedbackMessage({ text: error.message, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const renderPasswordStrength = () => {
    if (formState !== 'signup') return null;
    return (
      <div className="mt-2 text-sm text-cyan-400/70 space-y-1">
        <p className="flex items-center gap-2">
          {password.length >= 8 ? <Check size={14} /> : <AlertCircle size={14} />}
          Minimum 8 characters
        </p>
        <p className="flex items-center gap-2">
          {/[A-Z]/.test(password) ? <Check size={14} /> : <AlertCircle size={14} />}
          Uppercase letter
        </p>
        <p className="flex items-center gap-2">
          {/[0-9]/.test(password) ? <Check size={14} /> : <AlertCircle size={14} />}
          Number
        </p>
        <p className="flex items-center gap-2">
          {/[^A-Za-z0-9]/.test(password) ? <Check size={14} /> : <AlertCircle size={14} />}
          Special character
        </p>
      </div>
    );
  };

  const renderAuthForm = () => {
    const isSignup = formState === 'signup';

    return (
      <section className="w-full max-w-md mx-auto px-4">
        <div
          className="bg-black/60 border border-cyan-400/20 p-6 sm:p-8 group hover:border-cyan-400 transition-colors duration-300 rounded-lg"
        >
          <div className={`text-2xl sm:text-3xl font-bold mb-6 text-cyan-400 ${glowing ? 'animate-pulse' : ''}`}>
            {isSignup ? "SIGN UP" : "LOGIN"}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-cyan-400 mb-2">Username:</label>
              <Input
                icon={User}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            {isSignup && (
              <div className="space-y-2">
                <label className="block text-cyan-400 mb-2">Email:</label>
                <Input
                  icon={User}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-cyan-400 mb-2">Password:</label>
              <PasswordInput
                value={password}
                onChange={handlePasswordChange}
                required
              />
              {renderPasswordStrength()}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full border border-cyan-400 hover:bg-cyan-400/20 py-3 rounded transition-colors uppercase tracking-wider"
            >
              {isLoading ? 'LOADING...' : (isSignup ? 'SIGN UP' : 'LOGIN')}
            </button>

            {feedbackMessage.text && (
              <p className={`text-sm mt-4 flex items-center gap-2 ${feedbackMessage.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                <AlertCircle size={16} /> {feedbackMessage.text}
              </p>
            )}

            <div className="flex flex-col sm:flex-row justify-between text-sm text-slate-300 gap-2">
              <button
                type="button"
                onClick={() => {
                  setFormState(isSignup ? 'login' : 'signup');
                  setFeedbackMessage({ text: '', type: '' });
                  setUsername('');
                  setEmail('');
                  setPassword('');
                }}
                className="text-cyan-400 hover:text-cyan-300"
              >
                {isSignup ? 'Already have an account?' : "Don't have an account?"}
              </button>
              {!isSignup && (
                <button
                  type="button"
                  onClick={() => navigate('/reset-password')}
                  className="text-cyan-400 hover:text-cyan-300"
                >
                  Forgot Password?
                </button>
              )}
            </div>
          </form>
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Background elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 w-full h-full bg-blue-900 opacity-20"></div>
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-blue-400 blur-3xl opacity-20"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-blue-500 blur-3xl opacity-20"></div>
        <div className="absolute bottom-40 left-1/4 w-24 h-24 rounded-full bg-blue-300 blur-3xl opacity-10"></div>
      </div>

      <Navbar currentPage="LOGIN" />

      <main className="relative z-10 pt-20 flex-grow max-w-5xl mx-auto p-4 sm:p-6 space-y-20">
        <section className="text-center space-y-6 pt-10">
          <div className={`logo-container mb-8 ${glowing ? 'animate-pulse' : ''}`}>
            <div className="w-64 h-64 mx-auto relative">
              <div className="absolute inset-0 bg-cyan-400 opacity-20 rounded-full blur-lg"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="border-2 border-cyan-400 w-56 h-40 relative">
                  {/* Top diamond point */}
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rotate-45 border-2 border-cyan-400 bg-black"></div>
                  {/* Bottom diamond point */}
                  <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rotate-45 border-2 border-cyan-400 bg-black"></div>
                  
                  {/* Small circles */}
                  <div className="absolute -left-2 top-1/4 w-4 h-4 rounded-full border-2 border-cyan-400 bg-black"></div>
                  <div className="absolute -right-2 top-1/4 w-4 h-4 rounded-full border-2 border-cyan-400 bg-black"></div>
                  <div className="absolute -left-2 bottom-1/4 w-4 h-4 rounded-full border-2 border-cyan-400 bg-black"></div>
                  <div className="absolute -right-2 bottom-1/4 w-4 h-4 rounded-full border-2 border-cyan-400 bg-black"></div>
                  
                  {/* Text */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                    <div className="text-cyan-400 text-4xl font-bold tracking-wider leading-none">
                      <div>THE</div>
                      <div>MEDIOCRE</div>
                      <div>MAKERS</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold mb-4 text-white">
            Member <span className="text-cyan-400">Authentication</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mb-8 mx-auto">
            Join our mediocre community or log back in to continue your perfectly imperfect journey.
          </p>
        </section>

        {renderAuthForm()}
      </main>

      <footer className="bg-black py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className={`mb-8 ${glowing ? 'animate-pulse' : ''}`}>
              <div className="text-cyan-400 text-lg font-bold tracking-wider">THE MEDIOCRE MAKERS</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-bold mb-4">Navigate</h3>
                <ul className="space-y-2">
                  <li><a href="/" className="text-gray-400 hover:text-cyan-400 transition-colors">Home</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Social</h3>
                <ul className="space-y-2">
                  <li><a href="https://x.com/MediocreMakers" className="text-gray-400 hover:text-cyan-400 transition-colors">Twitter</a></li>
                </ul>
                <ul className="space-y-2">
                  <li><a href="https://www.youtube.com/@TheMediocreMakers/featured" className="text-gray-400 hover:text-cyan-400 transition-colors">Youtube</a></li>
                </ul>
                <ul className="space-y-2">
                  <li><a href="https://www.instagram.com/TheMediocreMakers" className="text-gray-400 hover:text-cyan-400 transition-colors">Instagram</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Support</h3>
                <ul className="space-y-2">
                  <li><a href="https://ko-fi.com/themediocremakers" className="text-gray-400 hover:text-cyan-400 transition-colors">Ko-Fi</a></li>
                </ul>
                <ul className="space-y-2">
                  <li><a href="https://gamejolt.com/@TheMediocreMakers" className="text-gray-400 hover:text-cyan-400 transition-colors">Gamejolt</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} The Mediocre Makers & 4FingerStudios. 
            </div>
          </div>
        </footer>
    </div>
  );
};

export default LoginPage;